import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  DeleteSessionParams,
  DeleteSessionData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface DeleteSessionState
  extends MergeRequestState<DeleteSessionData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.deleteSession,
  isFetching: (state: ReduxState) => state.server.deleteSession.isFetching,
  data: (state: ReduxState) => state.server.deleteSession.data,
  error: (state: ReduxState) => state.server.deleteSession.error,
};

const initialState: DeleteSessionState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/deleteSession';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: DeleteSessionParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.deleteSession(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerDeleteSession {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const deleteSession: ServerDeleteSession = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
